import { Component, computed, inject, Signal, signal, ViewChild, WritableSignal, OnInit, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { List, ListItem } from '@app/_interfaces/list.interface';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ListFormComponent } from '../list-form/list-form.component';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { ListStore, UserResourceStore, ViewSettingsStore } from '@app/_stores';
import { ListItemsComponent } from '../list-items/list-items.component';
import { SvgComponent } from '@app/components/shared/svg/svg.component';
import { InputDropdownComponent } from '@app/components/shared/inputs/input-dropdown/input-dropdown.component';
import { FilterHashtagComponent } from '@app/components/shared/filters/filter-hashtag/filter-hashtag.component';
import { CacheService } from '@app/_services/cache.service';
import { ViewSettingService } from '@app/_services/view-setting.service';
import { FilterSearchComponent } from '@app/components/shared/filters/filter-search/filter-search.component';
import { FilterRoleComponent } from '@app/components/shared/filters/filter-role/filter-role.component';
import { FilterStatusComponent } from '@app/components/shared/filters/filter-status/filter-status.component';
import { FilterDateRangeComponent } from '@app/components/shared/filters/filter-date-range/filter-date-range.component';
import { AlertService } from '@app/_services/alert.service';
import { DragScrollDirective } from '@app/_directives/drag-scroll.directive';
import { CollaboraterRole, EntityStatus, ShowType } from '@app/_types/generic.type';
import { MapService } from '@app/_services/map.service';
import { InputDropdownConfig } from '@app/_interfaces/generic.interface';
// MatTabsModule removed - unified view without tabs
import { MeUserAvatarComponent } from '../../../common/account/me-user-avatar/me-user-avatar.component';
import { SettingsService } from '@app/_services/settings.service';
import { NotificationComponent } from '@app/components/common/notifications/notification/notification.component';
import { ListBlockComponent } from '../list-block/list-block.component';
import { NoResultBlockComponent } from '@app/components/shared/no-result-block/no-result-block.component';
import { Subject, takeUntil } from 'rxjs';
import { AnalyticsService } from '@app/_services/analytics.service';
import { StorageService } from '@app/_services/storage.servive';
import { AnalyticsScreen } from '@app/_types/analytics-events.types';
import { MobileBottomBarComponent } from '@app/components/shared/mobile-bottom-bar/mobile-bottom-bar.component';
import { ListViewKebabComponent } from '../list-view-kebab/list-view-kebab.component';
import { ModalService } from '@app/_services/modal.service';

@Component({
  selector: 'app-list',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    SvgComponent,
    MatMenuModule,
    FilterSearchComponent,
    FilterHashtagComponent,
    FilterRoleComponent,
    FilterStatusComponent,
    FilterDateRangeComponent,
    DragScrollDirective,
    NotificationComponent,
    ListBlockComponent,
    NoResultBlockComponent,
    MobileBottomBarComponent
  ],
  templateUrl: './list.component.html',
  styleUrl: './list.component.scss'
})

export class ListComponent implements OnInit, OnDestroy {

  @ViewChild('menuTrigger', { static: false }) childMenu!: MatMenuTrigger;
  readonly listStore = inject(ListStore);
  readonly viewSettings = inject(ViewSettingsStore);
  readonly userResourceStore = inject(UserResourceStore);
  roleMap: Signal<Record<string, string>> = this.mapService.collaboratorRoleMap;

  // Unified config for lists (no tabs)
  config: {
    name: string;
    groupBy: string;
    groupedViewType: string;
    show: string;
    descriptionType: string;
    hiddenGroupValues: string[];
    hiddenShowValues: ShowType[];
    showConfig: { [key: string]: InputDropdownConfig };
  } = {
      name: this.ss.t('listsMyLists_title'),  // "My Lists" - could add 'common_lists' translation
      groupBy: 'listGroupBy',
      groupedViewType: 'listGroupedViewType',
      show: 'listShow',
      descriptionType: 'listDescriptionType',
      hiddenGroupValues: [],
      hiddenShowValues: [],  // Show all options including 'access' for shared items
      showConfig: this.vs.listShowConfig()
    };

  // Direct access to view settings (unified view)
  listGroupBy: Signal<string> = this.viewSettings.listGroupBy;
  listGroupedViewType: Signal<string[]> = this.viewSettings.listGroupedViewType;
  listShow: Signal<ShowType[]> = this.viewSettings.listShow;

  isFilter: boolean = false;
  filters: {
    filterQuery: WritableSignal<string>;
    accessRoles: WritableSignal<CollaboraterRole[]>;
    isFav: WritableSignal<boolean>;
    status: WritableSignal<EntityStatus[]>;
    tags: WritableSignal<string[]>;
    startDate: WritableSignal<Date | null>;
    endDate: WritableSignal<Date | null>;
  } = {
      filterQuery: signal<string>(''),
      accessRoles: signal<CollaboraterRole[]>([]),
      isFav: signal<boolean>(false),
      status: signal<EntityStatus[]>([]),
      tags: signal<string[]>([]),
      startDate: signal<Date | null>(null),
      endDate: signal<Date | null>(null),
    };

  openKebabMenu() {
    const dialogRef = this.modalService.openLevel3(ListViewKebabComponent, {
      config: this.config  // Single config, no activeTab
    });

    this.modalService.getClosedObservable(dialogRef).subscribe((dialogResult) => {
      if (dialogResult) {
        this.ss.updateView();
      }
    });
  }

  // Unified filtered lists (owned + shared combined)
  filteredLists: Signal<List[]> = computed(() => {
    const lists = this.listStore.allLists();
    const filters = this.filters;
    // Convert status array to isCompleted boolean for filterLists compatibility
    const statusArr = filters.status();
    let isCompleted: boolean | null = null;
    if (statusArr.length === 1) {
      isCompleted = statusArr[0] === 'completed';
    } else if (statusArr.length === 0 || statusArr.length > 1) {
      isCompleted = null;
    }
    // v150: Pass favoriteListIds from UserResources
    return this.listStore.filterLists(
      lists,
      filters.filterQuery(),
      filters.isFav(),
      this.userResourceStore.favoriteLists(),
      isCompleted,
      filters.tags(),
      filters.startDate(),
      filters.endDate()
    );
  });

  isFiltered: Signal<boolean> = computed(() => {
    const filters = this.filters;
    // Note: filters.isFav() is NOT included here because the favorite filter
    // button is now outside the filter row (in the button bar).
    // Only filters that appear IN the filter row should affect "Clear All" button.
    const isFilterApplied: boolean = filters.tags().length > 0 || filters.filterQuery() !== '' || filters.status().length > 0 || filters.accessRoles().length > 0 || filters.startDate() !== null || filters.endDate() !== null;
    return isFilterApplied;
  });

  /// True if any filter is applied (filter row OR favourite).
  /// Used for button visibility and empty state checks.
  isAnyFilterApplied: Signal<boolean> = computed(() => this.isFiltered() || this.filters.isFav());

  // Grouping signal - use unified filteredLists
  hashtagGroup: Signal<any[]> = computed(() => {
    const hashtags = this.ss.hashtags();
    const sortedLists = this.filteredLists();
    const hashtagMap = new Map(hashtags.map(({ id, tag }) => [id, tag]));

    // Group the list Data based on hashtags
    const { groupedMap, noHashtagLists } = sortedLists.reduce(
      (acc, list) => {
        const { groupedMap, noHashtagLists } = acc;

        if (!list.hashtags || list.hashtags.length === 0) {
          noHashtagLists.push(list);
        } else {
          // Filter only valid hashtags that exist in hashtagMap
          const validTags = list.hashtags.filter(tagId => hashtagMap.has(tagId));

          // If no valid tags (all hashtags were deleted), treat as no hashtag
          if (validTags.length === 0) {
            noHashtagLists.push(list);
          } else {
            // Group by valid hashtags only
            validTags.forEach(tagId => {
              const id = tagId || '';
              if (!groupedMap.has(id)) {
                groupedMap.set(id, {
                  id,
                  name: `#${hashtagMap.get(id)}`,
                  data: []
                });
              }
              groupedMap.get(id)!.data.push(list);
            });
          }
        }

        return acc;
      },
      {
        groupedMap: new Map<string, { id: string; name: string; data: List[] }>(),
        noHashtagLists: [] as List[]
      }
    );

    const groupedData = [...groupedMap.values()];

    if (noHashtagLists.length > 0) {
      groupedData.push({
        id: 'no-hashtags',
        name: this.ss.t('common_groupByNoHashtags'),
        data: noHashtagLists
      });
    }

    return groupedData;
  });

  unSubscribe = new Subject<void>();
  screenEnterTime: Signal<Date> = signal(new Date());

  constructor(
    public dialog: MatDialog,
    public cc: CacheService,
    public vs: ViewSettingService,
    private alertService: AlertService,
    private mapService: MapService,
    public ss: SettingsService,
    private as: AnalyticsService,
    private storageService: StorageService,
    private modalService: ModalService
  ) {

  }

  ngOnInit() {
    this.analyticsInit();
  }

  async toggleFilter() {
    const isFilter = this.isFilter;
    if (isFilter && this.isFiltered()) {
      const confirmed = await this.alertService.confirm(this.ss.t('filterApplied_title'), this.ss.t('filterApplied_content'), this.ss.t('common_proceed'), this.ss.t('common_buttonCancel'), 'color-14')
      if (!confirmed) {
        return;
      }
      this.clearFilters();
      this.isFilter = false;
    } else {
      this.isFilter = !isFilter;
    }
  }

  // Toggle favorite filter (called from favorite filter button)
  toggleFavoriteFilter() {
    this.filters.isFav.set(!this.filters.isFav());
  }

  clearFilters() {
    const filters = this.filters;
    this.isFilter = false;
    filters.filterQuery.set('');
    filters.accessRoles.set([]);
    filters.isFav.set(false);
    filters.status.set([]);
    filters.tags.set([]);
    filters.startDate.set(null);
    filters.endDate.set(null);
  }

  openListForm(list?: List) {
    this.modalService.openLevel3(ListFormComponent,
      {
        mode: list ? 'edit' : 'new',
        value: list,
      },
      { disableClose: true }
    );
  }

  toggleChildMenu(event: Event) {
    event.stopPropagation();
    this.childMenu.menuOpen ? this.childMenu.closeMenu() : this.childMenu.openMenu();
  }

  getIsOpen() {
    return this.listGroupedViewType().includes('collapsedView') ? 'false' : 'true';
  }

  toggleChildGroup(element: HTMLElement, groupId: string) {
    const isOpened = element.getAttribute('isOpen');
    const groupedElement = document.getElementById(groupId);
    if (!groupedElement) {
      return
    }
    if (isOpened === 'false') {
      groupedElement.setAttribute('isGroupOpened', 'true');
      element.setAttribute('isOpen', 'true');
    } else {
      groupedElement.setAttribute('isGroupOpened', 'false');
      element.setAttribute('isOpen', 'false');
    }
  }

  public openListItemsDialog(list: List) {
    const dialog = this.modalService.openLevel1(
      ListItemsComponent,
      {
        value: list,
        show: this.listShow()
      },
      { disableClose: true, autoFocus: false }
    )

    // return dialog.afterClosed();
  }

  analyticsInit() {
    this.as.analytics$.pipe(takeUntil(this.unSubscribe)).subscribe((init: boolean) => {
      if (init) {
        const previousScreen = this.storageService.getCurrentScreen() as AnalyticsScreen;
        this.as.screenViewEvent('lists', previousScreen);
        this.storageService.setCurrentScreen('lists');
      }
    });
  }

  ngOnDestroy() {
    this.as.screenExitEvent('lists', this.screenEnterTime());
    this.unSubscribe?.next();
    this.unSubscribe?.complete();
  }
}
